// BUILD-039 — Unique HQ Roles & Permissions: pure presentation rules (no React, no network), tested with `node --experimental-strip-types`.
// NOTHING here is authority: every change is decided again by the server, which refuses anything these rules would let a button through.
// These rules decide what the screens show as allowed, custom, locked or read-only, and turn checkbox states into a person's exceptions.

export interface PermissionDef { key: string; module: string; label: string; description: string; kind: 'configurable' | 'owner-only'; requiresCompanyAdmin: boolean; individualGrantable: boolean; sensitive: boolean }
export interface RoleDef { roleId: string; code: string; label: string; description: string; isSystem: boolean; privileged: boolean; allPermissions: boolean; legacyGroup: string | null; version: number; assignedCount: number; permissions: string[] }
export interface Viewer { companyAdmin: boolean; platformOwner: boolean; canChange?: boolean }
export type Effect = 'allow' | 'block';
export type Exceptions = Record<string, Effect>;

export type CellState =
  | { kind: 'editable'; on: boolean }
  | { kind: 'protected'; on: boolean; reason: string }
  | { kind: 'locked'; on: boolean; reason: string };   // could be edited, but not by this viewer

/** Shown on every permission screen while the platform has not opened permission writes: the screens are read-only, and this says why. */
export const WRITES_CLOSED_TEXT = 'Changing roles and permissions here is not open yet. It opens only after Unique HQ has been switched to the new permission system and checked. Until then you can look at roles and access, and nothing on this screen can be saved.';
export const PROTECTED_TEXT = {
  ownerAll: 'The Owner role always has everything except Vendors. It cannot be reduced.',
  ownerOnly: 'Owner only. Never given to a role or a person.',
  adminNeedsPlatformOwner: 'Only a Platform Owner can change the Admin role.',
  vendorsNeedCompanyAdmin: 'Only a Company Admin can change Vendors access.',
  readOnlyViewer: 'You can view access, but only a Company Admin or Platform Owner can change it.',
  companyAdminVendors: 'Company Admins always administer Vendors.',
} as const;

export const VENDOR_KEYS = ['vendors.view', 'vendors.maintain', 'vendors.administer'] as const;
const isVendorKey = (k: string) => (VENDOR_KEYS as readonly string[]).includes(k);
export type VendorLevel = 'none' | 'view' | 'maintain' | 'administer';
export const VENDOR_LEVEL_LABEL: Record<VendorLevel, string> = { none: 'No Vendors access', view: 'View Vendors', maintain: 'Maintain Vendors', administer: 'Administer Vendors' };

/** Permissions grouped by module, in catalog order (the catalog is already ordered). */
export function modulesOf(permissions: PermissionDef[]): Array<{ module: string; permissions: PermissionDef[] }> {
  const out: Array<{ module: string; permissions: PermissionDef[] }> = [];
  for (const p of permissions) {
    let g = out.find((x) => x.module === p.module);
    if (!g) { g = { module: p.module, permissions: [] }; out.push(g); }
    g.permissions.push(p);
  }
  return out;
}

/** The keys a role carries (the Owner role: everything except Owner-workspace checks are included, Vendors never). */
export function roleKeys(role: RoleDef, catalog: PermissionDef[]): string[] {
  return role.allPermissions ? catalog.filter((p) => !p.requiresCompanyAdmin).map((p) => p.key) : [...role.permissions];
}

/** What one Roles & Permissions matrix cell shows. `draft` is the set being edited for this role (or the saved set). */
export function cellFor(role: RoleDef, perm: PermissionDef, viewer: Viewer, draft: ReadonlySet<string>): CellState {
  if (role.allPermissions) return perm.requiresCompanyAdmin ? { kind: 'protected', on: false, reason: PROTECTED_TEXT.ownerAll } : { kind: 'protected', on: true, reason: PROTECTED_TEXT.ownerAll };
  const on = draft.has(perm.key);
  if (perm.kind === 'owner-only') return { kind: 'protected', on: false, reason: PROTECTED_TEXT.ownerOnly };
  if (viewer.canChange === false) return { kind: 'locked', on, reason: PROTECTED_TEXT.readOnlyViewer };
  if (role.privileged && !viewer.platformOwner) return { kind: 'locked', on, reason: PROTECTED_TEXT.adminNeedsPlatformOwner };
  if (perm.requiresCompanyAdmin && !viewer.companyAdmin) return { kind: 'locked', on, reason: PROTECTED_TEXT.vendorsNeedCompanyAdmin };
  return { kind: 'editable', on };
}

/** Vendors levels are one choice: Administer includes Maintain includes View. */
export function vendorLevelOf(keys: Iterable<string>): VendorLevel {
  const s = new Set(keys);
  return s.has('vendors.administer') ? 'administer' : s.has('vendors.maintain') ? 'maintain' : s.has('vendors.view') ? 'view' : 'none';
}
export function withVendorLevel(keys: Iterable<string>, level: VendorLevel): Set<string> {
  const s = new Set([...keys].filter((k) => !isVendorKey(k)));
  if (level === 'view' || level === 'maintain' || level === 'administer') s.add('vendors.view');
  if (level === 'maintain' || level === 'administer') s.add('vendors.maintain');
  if (level === 'administer') s.add('vendors.administer');
  return s;
}

/** Toggle one permission in a set. Vendors keys keep their order: checking a higher level includes the lower ones; unchecking a lower one drops the higher ones. */
export function toggle(set: ReadonlySet<string>, key: string): Set<string> {
  if (isVendorKey(key)) {
    const order: VendorLevel[] = ['none', 'view', 'maintain', 'administer'];
    const target: VendorLevel = key === 'vendors.view' ? 'view' : key === 'vendors.maintain' ? 'maintain' : 'administer';
    const below = order[order.indexOf(target) - 1];
    return withVendorLevel(set, set.has(key) ? below : target);
  }
  const s = new Set(set);
  if (s.has(key)) s.delete(key); else s.add(key);
  return s;
}

export function diffKeys(before: Iterable<string>, after: Iterable<string>): { added: string[]; removed: string[] } {
  const b = new Set(before); const a = new Set(after);
  return { added: [...a].filter((k) => !b.has(k)).sort(), removed: [...b].filter((k) => !a.has(k)).sort() };
}

// ── one person: checkboxes <-> exceptions ────────────────────────────────────────────────────────────────────────────
/** What the person can do = role defaults + allows - blocks. */
export function effectiveOf(defaults: Iterable<string>, exceptions: Exceptions): Set<string> {
  const s = new Set(defaults);
  for (const [k, e] of Object.entries(exceptions)) { if (e === 'allow') s.add(k); else s.delete(k); }
  return s;
}
/** The exceptions that turn the role defaults into exactly `desired` (a checked box equal to the default needs no exception). Only `editable` keys. */
export function exceptionsFor(defaults: Iterable<string>, desired: ReadonlySet<string>, editable: Iterable<string>): Exceptions {
  const d = new Set(defaults); const out: Exceptions = {};
  for (const k of [...editable].sort()) {
    const want = desired.has(k); const def = d.has(k);
    if (want && !def) out[k] = 'allow';
    if (!want && def) out[k] = 'block';
  }
  return out;
}
/** Merge: keep the person's existing exceptions on keys the viewer cannot edit; the editable keys follow the checkboxes. */
export function mergedExceptions(current: Exceptions, defaults: Iterable<string>, desired: ReadonlySet<string>, editable: Iterable<string>): Exceptions {
  const ed = new Set(editable);
  const kept = Object.fromEntries(Object.entries(current).filter(([k]) => !ed.has(k)));
  return { ...kept, ...exceptionsFor(defaults, desired, ed) };
}
export function sameExceptions(a: Exceptions, b: Exceptions): boolean {
  const ka = Object.keys(a).sort(); const kb = Object.keys(b).sort();
  return ka.length === kb.length && ka.every((k, i) => k === kb[i] && a[k] === b[k]);
}

export interface RowView { key: string; label: string; description: string; on: boolean; custom: boolean; roleDefault: boolean; locked: string | null; vendor: boolean }
export interface PersonContext {
  /** the named Vendors grant level; 'unknown' = it could not be read in full (never treated as "none") */
  viewer: Viewer; self: boolean; targetPrivileged: boolean; targetCompanyAdmin: boolean; namedVendorLevel: VendorLevel | 'unknown';
  /** a recorded SERVICE identity - the only kind of account whose access is not changed on this screen (a retention note on a human is information, not a lock) */
  primaryOwner: boolean; servicePrincipal: boolean; active: boolean;
}
/** Why the whole editor is read-only, or null. */
export function editorLock(ctx: PersonContext): string | null {
  if (ctx.viewer.canChange === false) return PROTECTED_TEXT.readOnlyViewer;
  if (ctx.self) return 'You cannot change your own access.';
  if (ctx.primaryOwner) return 'The Primary Owner\u2019s access cannot be changed.';
  if (ctx.servicePrincipal) return 'This is a service account. Its access is not changed on this screen.';
  if (!ctx.active) return 'This person is inactive. Reactivate them to change their access.';
  if (ctx.targetPrivileged && !ctx.viewer.platformOwner) return 'Only a Platform Owner can change the access of an Owner or Admin.';
  return null;
}
/** One checkbox row for a person. */
export function rowFor(perm: PermissionDef, defaults: ReadonlySet<string>, desired: ReadonlySet<string>, ctx: PersonContext): RowView {
  const vendor = perm.requiresCompanyAdmin;
  const base = { key: perm.key, label: perm.label, description: perm.description, vendor, roleDefault: defaults.has(perm.key) };
  if (perm.kind === 'owner-only') return { ...base, on: defaults.has(perm.key), custom: false, locked: PROTECTED_TEXT.ownerOnly };
  // A Company Admin has full Vendors access. A NAMED grant may still exist beneath that authority: the lock text must say so, not hide it.
  if (vendor && ctx.targetCompanyAdmin) {
    const n = ctx.namedVendorLevel;
    const tail = n === 'unknown' ? ' A direct Vendors grant could not be read in full (unknown).' : n !== 'none' ? ` A direct grant (${VENDOR_LEVEL_LABEL[n]}) also exists beneath it and is listed in the Vendors access history.` : '';
    return { ...base, on: true, custom: false, locked: PROTECTED_TEXT.companyAdminVendors + tail };
  }
  const on = desired.has(perm.key);
  const custom = on !== defaults.has(perm.key);
  const lock = editorLock(ctx);
  if (lock) return { ...base, on, custom, locked: lock };
  if (!perm.individualGrantable) return { ...base, on, custom, locked: 'Set by the role only.' };
  if (vendor && !ctx.viewer.companyAdmin) return { ...base, on, custom, locked: PROTECTED_TEXT.vendorsNeedCompanyAdmin };
  // Unknown is NOT "none": with a direct grant that could not be read (an older server) the rows stay locked rather than guessing.
  if (vendor && ctx.namedVendorLevel === 'unknown') return { ...base, on, custom, locked: 'A direct Vendors grant could not be read in full, so Vendors cannot be changed for this person right now.' };
  // A direct grant is a SOURCE, not a lock: its levels show as checked and can be unticked here (the reviewed save moves the grant into this checklist).
  return { ...base, on, custom, locked: null };
}

// ── Vendors: a legacy direct ("named") grant is one more SOURCE of the same checklist. The checklist is the only writer: a save that changes the Vendors level also
// names the grant it moves in (absorbNamedVendor), and the server removes it in the same decision. Nothing is granted or removed silently. ──
const VENDOR_RANK_BY_KEY: Record<string, number> = { 'vendors.view': 1, 'vendors.maintain': 2, 'vendors.administer': 3 };
export const VENDOR_LEVEL_BY_RANK: VendorLevel[] = ['none', 'view', 'maintain', 'administer'];
/** The Vendors keys a direct grant of this level implies (Administer includes Maintain includes View). */
export function namedImpliedKeys(level: VendorLevel | 'unknown'): string[] {
  if (level === 'unknown' || level === 'none') return [];
  const n = VENDOR_LEVEL_BY_RANK.indexOf(level);
  return VENDOR_KEYS.filter((k) => VENDOR_RANK_BY_KEY[k] <= n);
}
/** What the checklist shows for this person today: their saved exceptions PLUS an in-screen 'allow' for every level a direct grant gives that the role does not (never sent unless the save moves the grant). */
export function baselineWithNamed(current: Exceptions, defaults: Iterable<string>, level: VendorLevel | 'unknown'): Exceptions {
  const eff = effectiveOf(defaults, current); const out: Exceptions = { ...current };
  for (const k of namedImpliedKeys(level)) if (!eff.has(k)) out[k] = 'allow';
  return out;
}
export interface SavePlan { exceptions: Exceptions; absorb: string | null; vendorChanged: boolean }
/**
 * What to send. If the Vendors level the person ends with differs from what they have now (all sources), the direct grant is moved: the draft is sent in full with its grant id.
 * Otherwise the save is about other permissions: the Vendors exceptions stay exactly as saved and the direct grant is left alone.
 */
export function planSave(p: { current: Exceptions; baseline: Exceptions; pending: Exceptions; currentDefaults: Iterable<string>; defaults: Iterable<string>; namedGrantId: string | null }): SavePlan {
  const before = vendorLevelOf(effectiveOf(p.currentDefaults, p.baseline));
  const after = vendorLevelOf(effectiveOf(p.defaults, p.pending));
  const vendorChanged = before !== after;
  if (p.namedGrantId && vendorChanged) return { exceptions: { ...p.pending }, absorb: p.namedGrantId, vendorChanged };
  if (p.namedGrantId) {
    const keep = Object.fromEntries(Object.entries(p.pending).filter(([k]) => !isVendorKey(k)));
    const vend = Object.fromEntries(Object.entries(p.current).filter(([k]) => isVendorKey(k)));
    return { exceptions: { ...keep, ...vend } as Exceptions, absorb: null, vendorChanged };
  }
  return { exceptions: { ...p.pending }, absorb: null, vendorChanged };
}
/** Keys the viewer may change for this person (the rest keep their current exceptions). */
export function editableKeys(perms: PermissionDef[], defaults: ReadonlySet<string>, desired: ReadonlySet<string>, ctx: PersonContext): string[] {
  return perms.map((p) => rowFor(p, defaults, desired, ctx)).filter((r) => r.locked === null).map((r) => r.key);
}

/** Short account summary: "Designer/Sales" or "Designer/Sales · 1 added · 1 removed". */
export function summaryLine(roleLabel: string, exceptions: Exceptions): string {
  const add = Object.values(exceptions).filter((e) => e === 'allow').length;
  const rem = Object.values(exceptions).filter((e) => e === 'block').length;
  return [roleLabel, add ? `${add} added` : '', rem ? `${rem} removed` : ''].filter(Boolean).join(' \u00b7 ');
}

export function describeChange(p: { added: string[]; removed: string[] }, labelOf: (key: string) => string): string[] {
  const lines: string[] = [];
  if (p.added.length) lines.push(`Can now: ${p.added.map(labelOf).join(', ')}`);
  if (p.removed.length) lines.push(`Can no longer: ${p.removed.map(labelOf).join(', ')}`);
  if (!lines.length) lines.push('No change to what they can do.');
  return lines;
}

/** Which roles the viewer may pick for this person, and why the others are not offered. */
export function roleChoices(roles: RoleDef[], viewer: Viewer, currentRoleId: string | null, targetPrivileged: boolean): Array<{ role: RoleDef; disabled: string | null }> {
  return roles.map((role) => {
    if (currentRoleId === role.roleId) return { role, disabled: null };
    if (viewer.canChange === false) return { role, disabled: PROTECTED_TEXT.readOnlyViewer };
    if ((role.privileged || targetPrivileged) && !viewer.platformOwner) return { role, disabled: 'Only a Platform Owner can give or take away the Owner or Admin role.' };
    if (vendorLevelOf(role.permissions) !== 'none' && !viewer.companyAdmin) return { role, disabled: PROTECTED_TEXT.vendorsNeedCompanyAdmin };
    return { role, disabled: null };
  });
}

/**
 * Navigation fallback ONLY, used when the server's answer is not available (an older server): today's group behaviour (owner = everything but Vendors,
 * admin = everything but the Owner workspace / two owner-wide views / Vendors; other roles only as the FIRST group, pm = team work). The server never uses this.
 */
const OWNER_ONLY = ['hq.knowledge.governance-approve', 'hq.notes.use', 'hq.owner-strategic.use', 'hq.attention.view'];
const ADMIN_EXCLUDED = ['hq.work.team-all-departments', 'hq.health.all-indicators', ...OWNER_ONLY, ...VENDOR_KEYS];
export const NAV_KEYS = ['hq.users.manage', 'hq.documents.manage', 'hq.knowledge.review', 'hq.library.manage', 'hq.roadmap.manage', 'hq.health.upload',
  'hq.health.all-indicators', 'hq.work.team-view', 'hq.work.team-all-departments', 'hq.work.view-any', 'hq.work.manage-any', 'hq.supplier-pricing.admin',
  'hq.supplier-pricing.full-visibility', 'hq.integrations.view', 'hq.integrations.configure', 'hq.integrations.credentials', 'hq.integrations.run',
  'hq.automations.view', 'hq.automations.manage', 'hq.automations.run', 'hq.mailbox.admin-access', ...VENDOR_KEYS, ...OWNER_ONLY];
const LEGACY_ORDER = ['owner', 'admin', 'warranty', 'designer', 'pm', 'superintendent', 'accounting', 'external-sales'];
export function fallbackKeysFromGroups(groups: string[]): Set<string> {
  const held = LEGACY_ORDER.filter((g) => groups.includes(g));
  const s = new Set<string>();
  if (held.includes('owner')) for (const k of NAV_KEYS) if (!isVendorKey(k)) s.add(k);
  if (held.includes('admin')) for (const k of NAV_KEYS) if (!ADMIN_EXCLUDED.includes(k)) s.add(k);
  if (held[0] === 'pm') { s.add('hq.work.team-view'); s.add('hq.work.view-any'); }
  return s;
}
